import { Link } from "react-router-dom";
import ErrorSvg from "@/components/shared/svgs/error-svg";

type Props = {
  reason?: string;
  price?: number;
  courseName?: string;
  factorId?: string;
  createdAt?: string;
  purchaseSource?: string;
};

export default function FailedPayment({
  reason,
  price,
  courseName,
  factorId,
  createdAt,
  purchaseSource,
}: Props) {
  const displayPrice =
    price && price > 0
      ? `${price.toLocaleString("fa-IR")} تومان`
      : price === 0
        ? "رایگان"
        : "";

  // Render the receipt-style panel only when there's at least one field to
  // show — otherwise it would render as an empty box for the "user cancelled
  // before gateway confirm" case.
  const hasReceiptData = Boolean(
    displayPrice || factorId || createdAt || purchaseSource,
  );

  return (
    <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-default-100 bg-background shadow-lg shadow-default-900/5 print:mx-auto">
      <div className="relative flex flex-col items-center gap-3 px-4 pt-6 pb-4">
        <ErrorSvg />
        <p className="text-base font-bold text-default-900">پرداخت ناموفق</p>
      </div>

      <div className="relative h-3">
        <span className="absolute -right-1.5 top-1/2 size-3 -translate-y-1/2 rounded-full bg-secondary-background" />
        <span className="absolute -left-1.5 top-1/2 size-3 -translate-y-1/2 rounded-full bg-secondary-background" />
        <div className="absolute inset-x-3 top-1/2 -translate-y-1/2 border-t border-dashed border-default-200" />
      </div>

      <div className="flex flex-col gap-4 px-5 pb-5 pt-3">
        {/* Bank-supplied failure reason — styled with the danger tint to
            distinguish it from the receipt rows below. */}
        <div className="rounded-xl bg-danger/5 border border-danger/20 p-4 text-center text-sm leading-7 text-danger-700">
          {reason || "این تراکنش توسط مشتری لغو شد"}
        </div>

        {courseName ? (
          <p className="text-center text-sm leading-7 text-default-700">
            دوره{" "}
            <span className="font-bold text-default-900">{courseName}</span>
          </p>
        ) : null}

        {hasReceiptData ? (
          <div className="flex flex-col gap-6 rounded-xl bg-content3/40 p-4 text-xs">
            {displayPrice ? (
              <ReceiptRow label="مبلغ" value={displayPrice} />
            ) : null}
            {factorId ? (
              <ReceiptRow label="شماره پیگیری" value={factorId} mono />
            ) : null}
            {createdAt ? (
              <ReceiptRow label="تاریخ" value={createdAt} />
            ) : null}
            {purchaseSource ? (
              <ReceiptRow label="خرید از طریق" value={purchaseSource} />
            ) : null}
          </div>
        ) : null}

        <Link
          replace={true}
          className="block w-full rounded-xl bg-primary p-3.5 text-center text-sm font-bold text-white shadow-md shadow-primary/30 print:hidden"
          to="/"
        >
          بازگشت
        </Link>
      </div>
    </div>
  );
}

function ReceiptRow({
  label,
  value,
  mono,
}: {
  label: string;
  value?: string;
  mono?: boolean;
}) {
  return (
    <div className="flex w-full items-center gap-2">
      <span className="shrink-0 text-default-500">{label}</span>
      <span className="flex-1 border-b border-dotted border-default-300" />
      <span
        className={`shrink-0 font-semibold text-default-800 ${mono ? "font-mono tracking-tight" : ""}`}
        dir={mono ? "ltr" : undefined}
      >
        {value || "-"}
      </span>
    </div>
  );
}
